import { useState } from "react";
import axios from "axios";
import { Mail, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal } from "../components/Motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm text-ink placeholder:text-slate-400 outline-none transition-colors duration-300 focus:border-navy focus:ring-2 focus:ring-navy/10";

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="mb-2 block font-mono text-[0.65rem] font-semibold uppercase tracking-[0.22em] text-slate-400">
      {label}
    </span>
    {children}
  </label>
);

const Contact = () => {
  const { t } = useLanguage();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", company: "", email: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(c.success);
      setForm({ name: "", company: "", email: "", service: "", message: "" });
    } catch {
      toast.error(c.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <section className="relative overflow-hidden bg-paper pt-32 pb-16 md:pt-44 md:pb-24">
        <div className="blueprint-grid absolute inset-0 opacity-60" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-mono text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-accent">
                {c.overline}
              </p>
              <h1 className="mt-5 font-display text-4xl font-bold leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-6xl">
                {c.title}
              </h1>
              <p className="mt-6 max-w-md text-base leading-relaxed text-slate-600 md:text-lg">{c.sub}</p>
              <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
                <p className="font-mono text-[0.65rem] font-semibold uppercase tracking-[0.22em] text-slate-400">
                  {c.emailLabel}
                </p>
                <a
                  href="mailto:info@aldebran.com"
                  data-testid="contact-email-link"
                  className="mt-2 flex items-center gap-2.5 font-display text-xl font-semibold text-navy transition-colors duration-300 hover:text-accent"
                >
                  <Mail className="h-5 w-5 text-accent" />
                  info@aldebran.com
                </a>
              </div>
              <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
                <p className="font-mono text-[0.65rem] font-semibold uppercase tracking-[0.22em] text-slate-400">
                  {c.phoneLabel}
                </p>
                <a
                  href="tel:+390350167428"
                  data-testid="contact-phone-link"
                  className="mt-2 flex items-center gap-2.5 font-display text-xl font-semibold text-navy transition-colors duration-300 hover:text-accent"
                >
                  <Phone className="h-5 w-5 text-accent" />
                  +39 035 016 7428
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.12}>
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="rounded-2xl border border-slate-200 bg-white p-7 shadow-xl md:p-10"
              >
                <h2 className="font-display text-2xl font-semibold tracking-tight text-ink">{c.formTitle}</h2>
                <div className="mt-8 grid gap-6 sm:grid-cols-2">
                  <Field label={c.name} testid="contact-field-name">
                    <input
                      required
                      value={form.name}
                      onChange={set("name")}
                      data-testid="contact-name-input"
                      className={inputCls}
                      placeholder={c.name}
                    />
                  </Field>
                  <Field label={c.company} testid="contact-field-company">
                    <input
                      value={form.company}
                      onChange={set("company")}
                      data-testid="contact-company-input"
                      className={inputCls}
                      placeholder={c.company}
                    />
                  </Field>
                  <Field label={c.email} testid="contact-field-email">
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      data-testid="contact-email-input"
                      className={inputCls}
                      placeholder="nome@azienda.com"
                    />
                  </Field>
                  <Field label={c.service} testid="contact-field-service">
                    <select
                      value={form.service}
                      onChange={set("service")}
                      data-testid="contact-service-select"
                      className={inputCls}
                    >
                      <option value="">{c.servicePlaceholder}</option>
                      {t.services.map((s) => (
                        <option key={s.slug} value={s.title}>
                          {s.title}
                        </option>
                      ))}
                      <option value="CertificoQR">CertificoQR</option>
                    </select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label={c.message} testid="contact-field-message">
                      <textarea
                        required
                        rows={5}
                        value={form.message}
                        onChange={set("message")}
                        data-testid="contact-message-input"
                        className={`${inputCls} resize-none`}
                        placeholder={c.messagePlaceholder}
                      />
                    </Field>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="group mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-navy px-7 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-navy-dark disabled:opacity-60 sm:w-auto"
                >
                  {sending ? c.sending : c.submit}
                  <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <p className="mt-5 text-xs leading-relaxed text-slate-400" data-testid="contact-demo-note">
                  {c.note}
                </p>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
